import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Section } from "@/components/Section";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FAQ } from "@/components/FAQ";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/lib/site";
import { seoTitle, seoDescription } from "@/lib/seo-meta";

const TITLE = seoTitle("Contato — Fale com a redação da Gazeta Regional Online");
const DESC = seoDescription(
  "Sugestões de pauta, denúncias, correções e parcerias. Fale com a redação da Gazeta Regional Online — respondemos por e-mail em até 48 horas úteis.",
);

const CONTACT_FAQ = [
  { q: "Como sugiro uma pauta?", a: "Envie a sugestão pelo formulário ou por e-mail com o máximo de contexto: tema, cidade, fontes que a gente pode consultar e por que isso interessa ao leitor." },
  { q: "Aceitam publieditorial ou publi de marca?", a: "Aceitamos publieditoriais claramente identificados, mas nunca alteramos a linha editorial das matérias. Reviews e comparativos são sempre independentes." },
  { q: "Encontrei um erro em uma matéria. Como reporto?", a: "Escreva com o link da matéria e o trecho corrigido. A redação avalia e, quando cabe, publica a correção com data e explicação ao final do texto." },
  { q: "Quanto tempo para responder?", a: "Até 48 horas úteis. Fim de semana e feriado costumam atrasar." },
];

export const Route = createFileRoute("/contato")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "/contato" },
    ],
    links: [{ rel: "canonical", href: `${SITE.baseUrl}/contato` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: `Contato — ${SITE.name}`,
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "editorial",
            availableLanguage: "Portuguese",
            email: SITE.email,
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: CONTACT_FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Contato,
});

function Contato() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ nome: "", email: "", assunto: "Sugestão de pauta", mensagem: "" });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const subject = encodeURIComponent(`[Contato site] ${form.assunto} — ${form.nome}`);
    const body = encodeURIComponent(`${form.mensagem}\n\n— ${form.nome} (${form.email})`);
    window.open(`mailto:${SITE.email}?subject=${subject}&body=${body}`, "_blank", "noopener");
    setSubmitted(true);
  }

  return (
    <>
      <section className="hero-bg">
        <div className="container-svg pt-12 pb-12 md:pt-20 md:pb-16">
          <Breadcrumbs items={[{ label: "Capa", to: "/" }, { label: "Contato" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="eyebrow">Fale com a redação</span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl">
              <span className="text-gradient">Sugestões, denúncias e parcerias</span>
            </h1>
            <p className="mt-4 text-muted-foreground md:text-lg">
              Quer sugerir uma pauta, apontar uma correção, denunciar um problema de interesse público ou propor parceria? Escreva para a redação da Gazeta Regional Online.
            </p>
          </div>
        </div>
      </section>

      <Section>
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr]">
          <Reveal>
            <form onSubmit={onSubmit} className="card-glass space-y-4">
              <div>
                <label htmlFor="nome" className="text-sm font-semibold">Nome</label>
                <input id="nome" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  className="mt-1 w-full rounded border border-border bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none" />
              </div>
              <div>
                <label htmlFor="email" className="text-sm font-semibold">E-mail</label>
                <input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full rounded border border-border bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none" />
              </div>
              <div>
                <label htmlFor="assunto" className="text-sm font-semibold">Assunto</label>
                <select id="assunto" value={form.assunto} onChange={(e) => setForm({ ...form, assunto: e.target.value })}
                  className="mt-1 w-full rounded border border-border bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none">
                  <option>Sugestão de pauta</option>
                  <option>Correção em matéria</option>
                  <option>Denúncia / interesse público</option>
                  <option>Parceria / publieditorial</option>
                  <option>Fale com a redação</option>
                  <option>Outro</option>
                </select>
              </div>
              <div>
                <label htmlFor="mensagem" className="text-sm font-semibold">Mensagem</label>
                <textarea id="mensagem" rows={5} required value={form.mensagem} onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
                  className="mt-1 w-full rounded border border-border bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none" />
              </div>
              <button type="submit" className="btn-primary w-full">Enviar mensagem</button>
              {submitted && <p className="text-sm text-muted-foreground">Estamos abrindo seu e-mail. Caso não abra, escreva direto para {SITE.email}.</p>}
            </form>
          </Reveal>

          <div className="space-y-4">
            <Reveal delay={1}>
              <div className="card-glass border-l-4 border-l-accent">
                <h2 className="text-lg font-semibold">E-mail da redação</h2>
                <p className="mt-2 text-sm text-muted-foreground">A forma mais rápida de chegar até a gente:</p>
                <p className="mt-3 text-base">
                  <a href={`mailto:${SITE.email}`} className="text-primary hover:underline font-semibold">{SITE.email}</a>
                </p>
                <p className="mt-3 text-xs text-muted-foreground">Resposta em até 48 horas úteis.</p>
              </div>
            </Reveal>
            <Reveal delay={2}>
              <div className="card-glass border-l-4 border-l-accent">
                <h2 className="text-lg font-semibold">Só quer ler as notícias?</h2>
                <p className="mt-2 text-sm text-muted-foreground">Vá direto para as editorias com as matérias mais recentes.</p>
                <div className="mt-4"><a href="/blog" className="btn-accent w-full">Ir para as editorias</a></div>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      <Section>
        <FAQ items={CONTACT_FAQ} />
      </Section>
    </>
  );
}
